import React, {useState} from 'react';
import graphql from "babel-plugin-relay/macro";
import {toast} from "react-toastify";
import {useFragment, useMutation} from "react-relay";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import FormModal from "../common/modals/FormModal";
import HorizontalFormInput from "../common/horizontal-form-inputs/HorizontalFormInput";
import HorizontalFormSelect from "../common/horizontal-form-inputs/HorizontalFormSelect";
import {allAttachmentCategoriesDropdown} from "../../relay/Fragments";
import {createOptions} from "../../helpers/utils";

const NewAttachmentModal = ({showModal, onClose, parentId, allAttachmentCategories}) => {
    const mutation = graphql`
    mutation NewAttachmentModalMutation($parentId: ID!,  $fileName: String!, $size: Long!, $lastModifiedTimestampUtc: DateTime!, $attachmentCategoryId:ID!) {
        createAttachment(input: {parentId: $parentId, attachmentCategoryId: $attachmentCategoryId, fileName: $fileName, size: $size, lastModifiedTimestampUtc: $lastModifiedTimestampUtc})
        {
            attachment {
                id
            }
            uploadUrl
        }}`
    const reportStatusMutation = graphql`
        mutation NewAttachmentModalReportStatusMutation($parentId: ID!, $attachmentId: ID!, $uploadSuccessful:Boolean!) {
            reportAttachmentUploadStatus(input: {parentId: $parentId, attachmentId: $attachmentId, uploadSuccessful: $uploadSuccessful})
            {
                parent {
                    id
                    ... on LegalContract {
                        attachments {
                            ...Attachments
                        }
                    }
                    ... on NetworkOwner {
                        attachments {
                            ...Attachments
                        }
                    }
                    ... on Network {
                        attachments {
                            ...Attachments
                        }
                    }
                    ... on Organization {
                        attachments {
                            ...Attachments
                        }
                    }
                }
            }
        }`;

    const [commitMutation, isInFlight] = useMutation(mutation);
    const [commitReportStatusMutation, reportIsInFlight] = useMutation(reportStatusMutation);
    const [isUploading, setIsUploading] = useState(false);
    const categories = createOptions(useFragment(allAttachmentCategoriesDropdown, allAttachmentCategories), "id", "name");
    const defaultValues = {
        parentId: parentId,
        attachmentCategoryId: '',
        fileName: ''
    }
    const schema = yup.object({
        attachmentCategoryId: yup.string().required("Category is required")
    })
    const methods = useForm({defaultValues: defaultValues, resolver: yupResolver(schema)});

    const handleSubmit = (data) => {
        let fileUploaded = data.fileName[0];
        let fileName = fileUploaded.name;
        commitMutation({
            variables: {
                parentId: data.parentId,
                attachmentCategoryId: data.attachmentCategoryId,
                fileName: fileName,
                size: fileUploaded.size,
                lastModifiedTimestampUtc: new Date(fileUploaded.lastModified)
            },
            onCompleted: (returnedData) => {
                if (returnedData.createAttachment) {
                    let attachmentId = returnedData.createAttachment.attachment.id;
                    setIsUploading(true);
                    fetch(returnedData.createAttachment.uploadUrl, {
                        method: 'PUT', headers: {
                            "Content-Type": "application/octet-stream",
                            "x-ms-blob-type": "BlockBlob",
                            "Date": new Date().toUTCString()
                        }, body: data.fileName[0], mode: "cors"
                    }).then(response => {
                        let uploadSuccessful = response.ok;
                        setIsUploading(false);
                        commitReportStatusMutation({
                            variables: {
                                parentId: data.parentId,
                                attachmentId: attachmentId,
                                uploadSuccessful: uploadSuccessful
                            },
                            onCompleted: (returnedReportStatusData) => {
                                if (uploadSuccessful) {
                                    toast.success("Attachment " + fileName + " added");
                                } else {
                                    toast.error("Attachment " + fileName + " could not be added");
                                }
                                methods.reset();
                                onClose();
                            }
                        });
                    });
                }
            }
        })
    }
    return (
        <FormModal showModal={showModal}
                   onClose={onClose}
                   title={"New Attachment"}
                   methods={methods}
                   isInFlight={isInFlight || reportIsInFlight || isUploading}
                   onSubmit={handleSubmit}
                   buttonText={"Create"}
                   inFlightButtonText={"Creating"}
        >
            <HorizontalFormInput controlId={"fileName"} label={"File Name"} type={'file'}/>
            <HorizontalFormSelect options={categories} controlId={"attachmentCategoryId"} label={"Category"} />
        </FormModal>
    )
}
export default NewAttachmentModal